import classnames from 'classnames';
import { debounce } from 'lodash-es';
import { useCallback, useMemo } from 'react';

import InputText from 'forms/shared/inputText';
import Logger from 'logging/Logger';
import { Route } from 'store/routing/routes';
import { RouterProps, withRouter } from 'constants/reactRouter';
import { getFilteredUrlParams, isMatchingRoute, paramsToQueryString } from 'utils/urlUtils';
import { t } from 'utils/intlUtils';

import style from './startingBidRange.scss';

export interface LocationQuery {
  startingBidGTE: string;
  startingBidLTE: string;
}

interface Props extends RouterProps<LocationQuery> {
  /** CSS style for the title. */
  titleClassName?: string;
}

const StartingBidRange = ({ location, router, titleClassName }: Props) => {
  /**
   * Handles the onChange event for the input fields.
   */
  const onChange = useMemo(
    () =>
      debounce((paramName, value) => {
        const filteredParams = getFilteredUrlParams([paramName]);
        const paramsNext = paramsToQueryString({
          ...filteredParams,
          ...(value !== '' && Number(value) > -1 && { [paramName]: value }),
        });

        // Update URL params
        router?.push(`${location?.pathname}?${paramsNext}`);
      }, 250),
    [location?.pathname, router]
  );

  /**
   * Handles the onFocus event for the input fields.
   */
  const handleFocus = useCallback((inputName: string) => {
    Logger.trackUserAction(`filter_current_bid_amount_${inputName}_click`);
  }, []);

  return (
    <>
      <div className={style.container} data-testid="starting-bid-range">
        <div className={classnames(style.title, titleClassName)}>
          {isMatchingRoute(location, [Route.BROWSE, Route.BUY_TIMED_AUCTION]) ? t('current_bid') : t('starting_bid')}
        </div>
        <div className={style.inputs}>
          <InputText
            className={style.input}
            defaultValue={location?.query?.startingBidGTE || undefined}
            onChange={useCallback((value) => onChange('startingBidGTE', value), [onChange])}
            onFocus={() => handleFocus('min')}
            placeholder={t('min_price')}
            textClassName={style.inputText}
            type="number"
          />
          <span>{t('to')}</span>
          <InputText
            className={style.input}
            defaultValue={location?.query?.startingBidLTE || undefined}
            onChange={useCallback((value) => onChange('startingBidLTE', value), [onChange])}
            onFocus={() => handleFocus('max')}
            placeholder={t('max_price')}
            textClassName={style.inputText}
            type="number"
          />
        </div>
      </div>
    </>
  );
};

export default withRouter(StartingBidRange);
